import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

const ROLE_LABEL = {
  admin: "Administrateur", president: "Président", manager: "Responsable Gestion",
  treasurer: "Trésorier", secretary: "Secrétaire", member: "Membre",
};

export function AuthProvider({ children }) {
  // null = checking, false = anon, object = user
  const [user, setUser] = useState(null);
  const lastRolesRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      // Detect a role change vs previous snapshot — show a toast
      const newRoles = JSON.stringify((data.roles || []).slice().sort());
      if (lastRolesRef.current && lastRolesRef.current !== newRoles) {
        try {
          const labels = (data.roles || []).map((r) => ROLE_LABEL[r] || r).join(" · ");
          toast.success(`Vos rôles ont changé : ${labels}`, {
            description: "Reconnectez-vous pour que tous vos espaces soient pris en compte.",
            duration: 8000,
          });
        } catch { /* ignore */ }
      }
      lastRolesRef.current = newRoles;
      setUser(data);
    } catch {
      setUser(false);
      lastRolesRef.current = null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll /auth/me every 60s while logged in to detect role changes initiated by another admin
  useEffect(() => {
    if (!user || user === false) return;
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [user, refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name, extra = {}) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name, ...extra });
      // Registration always requires email verification + admin validation; do NOT set user.
      return { ok: true, pending: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
